import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, CaretUp, CaretDown, CaretUpDown, Trash, Bell, PaperPlaneTilt, CalendarX } from "@phosphor-icons/react";
import { toast } from "sonner";
import { StatusFilter } from "@/components/StatusFilter";
import { usePersistedState } from "@/lib/usePersistedState";
import { WorkerCombobox } from "@/components/WorkerCombobox";
import { Combobox } from "@/components/ui/combobox";
import { sortClientsByName } from "@/lib/comboboxHelpers";
import { lastFirst, lastNameKey } from "@/lib/names";
import { useColumnPrefs } from "@/lib/useColumnPrefs";
import { ColumnChooser } from "@/components/ColumnChooser";
import { shortDate, weekRangeLabel } from "@/lib/weeks";
import { EndAssignmentModal, ProjectNotCompletedDialog } from "@/components/EndAssignmentModal";

const empty = { worker_id: "", project_id: "", pay_rate: 0, bill_rate: 0, per_diem_rate: 0, ot_pay_rate: 0, ot_bill_rate: 0, ot_threshold_hours: 0, holiday_pay_rate: 0, holiday_bill_rate: 0, status: "active", start_date: "", expected_end_date: "" };

const ASN_COLUMNS = [
  { key: "worker", label: "Worker", locked: true },
  { key: "project", label: "Project" },
  { key: "location", label: "Location" },
  { key: "po", label: "PO #" },
  { key: "end", label: "End assignment" },
  { key: "pay", label: "Pay" },
  { key: "bill", label: "Bill" },
  { key: "per_diem", label: "Per Diem" },
  { key: "ts", label: "Timesheet" },
  { key: "status", label: "Status" },
];

const SortHeader = ({ label, active, dir, align = "left", onClick, testId }) => {
  const Icon = !active ? CaretUpDown : dir === "asc" ? CaretUp : CaretDown;
  return (
    <th className={`px-4 py-3 select-none ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className={`inline-flex items-center gap-1 group ${active ? "text-slate-900" : "text-slate-500 hover:text-slate-900"}`}
      >
        <span className="uppercase tracking-widest text-xs font-bold">{label}</span>
        <Icon size={12} weight={active ? "bold" : "regular"} className={active ? "opacity-100" : "opacity-40 group-hover:opacity-80"} />
      </button>
    </th>
  );
};

export default function Assignments() {
  const { user } = useAuth();
  const readOnly = user.role !== "admin";
  const [rows, setRows] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [otOn, setOtOn] = useState(false);
  const [holOn, setHolOn] = useState(false);
  const [err, setErr] = useState("");
  const [statusFilter, setStatusFilter] = usePersistedState("sos-filter-assignments", "active");
  const [sort, setSort] = useState({ col: "worker", dir: "asc" });
  const [nudgingId, setNudgingId] = useState(""); // assignment_id currently nudging
  const [nudgingAll, setNudgingAll] = useState(false);
  const [busyId, setBusyId] = useState(""); // resend notice / no-work toggle in flight
  const [sendNotice, setSendNotice] = useState(true);
  const [workerEmail, setWorkerEmail] = useState("");
  const { vis, hiddenSet, toggleColumn, resetColumns } = useColumnPrefs("assignments", ASN_COLUMNS);
  const asnVisCount = ASN_COLUMNS.length - hiddenSet.size + 1;
  const needsNudge = (r) => !isDone(r.status) && !r.open_timesheet && !r.no_work_this_week;

  const nudgeAll = async () => {
    const n = rows.filter(needsNudge).length;
    if (!window.confirm(`Send a timesheet reminder (email + SMS) to all ${n} worker(s) currently showing "No timesheet"?\n\nWorkers marked "No work" for last week are skipped.`)) return;
    setNudgingAll(true);
    try {
      const { data } = await api.post("/assignments/nudge-all");
      if (data.nudged.length > 0) {
        toast.success(`Nudged ${data.nudged.length} worker(s): ${data.nudged.map((x) => x.worker).join(", ")}`);
      } else {
        toast.info("No one to nudge — everyone has a timesheet in.");
      }
      if (data.skipped.length > 0) {
        toast.warning(`Skipped ${data.skipped.length}: ${data.skipped.map((s) => `${s.worker} (${s.reason})`).join("; ")}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally {
      setNudgingAll(false);
    }
  };

  const load = () => Promise.all([api.get("/assignments"), api.get("/workers"), api.get("/projects")]).then(([a, w, p]) => {
    setRows(Array.isArray(a.data) ? a.data : []);
    setWorkers(Array.isArray(w.data) ? w.data : []);
    setProjects(Array.isArray(p.data) ? p.data : []);
  });
  useEffect(() => { load(); }, []);

  const selectedWorker = workers.find((w) => w.id === form.worker_id);
  const workerNeedsEmail = !editingId && sendNotice && !!selectedWorker && !selectedWorker.email;

  const save = async (e) => {
    e.preventDefault(); setErr("");
    if (workerNeedsEmail && !workerEmail.trim()) {
      setErr("This worker has no email on file. Add one below to send the assignment notice, or untick “Send assignment notice”.");
      return;
    }
    const payload = { ...form,
      pay_rate: parseFloat(form.pay_rate) || 0,
      bill_rate: parseFloat(form.bill_rate) || 0,
      per_diem_rate: parseFloat(form.per_diem_rate) || 0,
      ot_pay_rate: otOn ? (parseFloat(form.ot_pay_rate) || 0) : 0,
      ot_bill_rate: otOn ? (parseFloat(form.ot_bill_rate) || 0) : 0,
      ot_threshold_hours: otOn ? (parseFloat(form.ot_threshold_hours) || 0) : 0,
      holiday_pay_rate: holOn ? (parseFloat(form.holiday_pay_rate) || 0) : 0,
      holiday_bill_rate: holOn ? (parseFloat(form.holiday_bill_rate) || 0) : 0,
    };
    try {
      if (editingId) await api.put(`/assignments/${editingId}`, payload);
      else {
        const { data } = await api.post("/assignments", {
          ...payload, send_notice: sendNotice,
          worker_email: workerNeedsEmail ? workerEmail.trim() : undefined,
        });
        if (data.notice) {
          if (data.notice.ok) toast.success(noticeSummary(data.notice));
          else toast.error(`Assignment created, but the notice was NOT sent: ${data.notice.error}. Use “Resend notice” on the row.`);
        }
      }
      setOpen(false); setForm(empty); setEditingId(null); setWorkerEmail(""); load();
    } catch (e) { setErr(e.response?.data?.detail?.toString() || e.message); }
  };
  const noticeSummary = (n) => {
    const smsBit = n.sms_sent ? "text sent" : (n.worker_phone ? "text failed" : "no phone on file");
    return `Assignment notice sent to ${n.worker_name} (${n.email}) — email + ${smsBit}${n.login_created ? " · login created, set-password link included" : ""}`;
  };
  const resendNotice = async (a) => {
    if (busyId) return;
    if (!window.confirm(`Resend the assignment notice to ${wname(a.worker_id)} for ${pname(a.project_id)}?\n\nThey'll get an email + text with the project details, a login link and a fresh single-use set-password link (7 days).`)) return;
    setBusyId(a.id);
    try {
      const { data } = await api.post(`/assignments/${a.id}/notice`);
      toast.success(noticeSummary(data));
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setBusyId(""); }
  };
  const toggleNoWork = async (a, on) => {
    if (busyId) return;
    setBusyId(a.id);
    try {
      const { data } = await api.post(`/assignments/${a.id}/no-work`, { on });
      toast.success(on
        ? `${wname(a.worker_id)} marked “No work” for the week of ${weekRangeLabel(data.week_start)} — reminders paused for that week.`
        : `“No work” cleared for ${wname(a.worker_id)} — reminders resume.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setBusyId(""); }
  };
  const wname = (id) => workers.find((w) => w.id === id)?.name || "—";
  const pname = (id) => projects.find((p) => p.id === id)?.name || "—";
  const ponum = (id) => projects.find((p) => p.id === id)?.po_number || "";
  const ploc = (id) => projects.find((p) => p.id === id)?.location || "";
  const preset = (pid) => {
    const p = projects.find((x) => x.id === pid);
    if (!p) return;
    setForm((f) => ({ ...f, project_id: pid, pay_rate: p.default_pay_rate, bill_rate: p.default_bill_rate, per_diem_rate: p.default_per_diem, ot_pay_rate: p.default_ot_pay_rate || 0, ot_bill_rate: p.default_ot_bill_rate || 0, ot_threshold_hours: p.ot_threshold_hours || 0, holiday_pay_rate: p.holiday_pay_rate || 0, holiday_bill_rate: p.holiday_bill_rate || 0 }));
    // Auto-reveal the OT / holiday fields only when the project actually has rates.
    setOtOn(!!(p.default_ot_pay_rate || p.default_ot_bill_rate));
    setHolOn(!!(p.holiday_pay_rate || p.holiday_bill_rate));
  };

  const [endFor, setEndFor] = useState(null);
  const [endResult, setEndResult] = useState(null);
  const endAssignment = (a) => setEndFor(a);
  const othersOn = (a) => rows.filter((r) => r.project_id === a.project_id && r.id !== a.id && !isDone(r.status)).map((r) => wname(r.worker_id));
  const reactivate = async (a) => {
    await api.put(`/assignments/${a.id}`, {
      worker_id: a.worker_id, project_id: a.project_id,
      pay_rate: a.pay_rate, bill_rate: a.bill_rate, per_diem_rate: a.per_diem_rate,
      start_date: a.start_date, end_date: a.end_date, expected_end_date: a.expected_end_date || "", status: "active",
    });
    load();
  };
  const isDone = (s) => s === "completed" || s === "ended";

  const tsBadge = (a) => {
    if (isDone(a.status)) return <span className="text-xs text-slate-300">—</span>;
    const ot = a.open_timesheet;
    const base = "inline-block px-2 py-0.5 rounded text-xs font-semibold uppercase whitespace-nowrap";
    if (!ot && a.no_work_this_week) return <span className={`${base} bg-blue-100 text-blue-800`} data-testid={`ts-no-work-${a.id}`} title={`Worker had no work the week of ${weekRangeLabel(a._nudge_week)} — reminders paused for that week.`}>No work · {shortDate(a._nudge_week)}</span>;
    if (!ot) return <span className={`${base} bg-slate-100 text-slate-500`} data-testid={`ts-none-${a.id}`}>No timesheet</span>;
    const wk = ot.week_start ? ` · ${new Date(ot.week_start + "T00:00:00").toLocaleDateString(undefined, { month: "numeric", day: "numeric" })}` : "";
    if (ot.status === "approved") return <span className={`${base} bg-green-100 text-green-800`} data-testid={`ts-approved-${a.id}`}>Approved{wk}</span>;
    if (ot.status === "pending") return <span className={`${base} bg-amber-100 text-amber-800`} data-testid={`ts-submitted-${a.id}`}>Submitted{wk}</span>;
    if (ot.status === "rejected") return <span className={`${base} bg-red-100 text-red-800`} data-testid={`ts-rejected-${a.id}`}>Rejected{wk}</span>;
    return <span className={`${base} bg-slate-100 text-slate-600`}>{ot.status}{wk}</span>;
  };

  const del = async (a) => {
    if (!window.confirm(`Delete this assignment (${wname(a.worker_id)} → ${pname(a.project_id)})?\n\nThis cannot be undone. If any timesheets are attached, the delete will be refused — use End assignment instead.`)) return;
    try {
      await api.delete(`/assignments/${a.id}`);
      toast.success("Assignment deleted.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
  };

  // Fire an email + SMS to the worker asking them to submit hours for
  // the current business week. Backend is /api/assignments/{id}/nudge — it
  // computes the current Monday in America/New_York and drops the login URL.
  const nudgeTimesheet = async (a) => {
    if (nudgingId) return;
    setNudgingId(a.id);
    try {
      const { data } = await api.post(`/assignments/${a.id}/nudge`);
      const smsBit = data.sms_sent ? "SMS sent" : (data.worker_phone ? "SMS failed" : "no phone on file");
      toast.success(`Timesheet reminder sent to ${data.worker_name} — email + ${smsBit}`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally {
      setNudgingId("");
    }
  };

  const toggleSort = (col) => setSort((s) => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }));

  const statusCounts = useMemo(() => ({
    all: rows.length,
    active: rows.filter((r) => !isDone(r.status)).length,
    completed: rows.filter((r) => isDone(r.status)).length,
  }), [rows]);

  const visibleRows = useMemo(() => {
    let base = rows;
    if (statusFilter === "active") base = rows.filter((r) => !isDone(r.status));
    else if (statusFilter === "completed") base = rows.filter((r) => isDone(r.status));
    const sortKey = (a) => {
      if (sort.col === "worker") return lastNameKey(wname(a.worker_id));
      if (sort.col === "project") return pname(a.project_id).toLowerCase();
      if (sort.col === "location") return ploc(a.project_id).toLowerCase();
      if (sort.col === "po") return ponum(a.project_id).toLowerCase();
      if (sort.col === "pay") return Number(a.pay_rate) || 0;
      if (sort.col === "bill") return Number(a.bill_rate) || 0;
      if (sort.col === "per_diem") return Number(a.per_diem_rate) || 0;
      if (sort.col === "ts") {
        const order = { rejected: 1, pending: 2, approved: 3 };
        if (a.open_timesheet) return order[a.open_timesheet.status] || 4;
        return a.no_work_this_week ? 0.5 : 0;
      }
      if (sort.col === "status") return isDone(a.status) ? "completed" : (a.status || "");
      return "";
    };
    const sorted = [...base].sort((x, y) => {
      const kx = sortKey(x), ky = sortKey(y);
      if (kx < ky) return sort.dir === "asc" ? -1 : 1;
      if (kx > ky) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, statusFilter, sort, workers, projects]);

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-start justify-between mb-6">
        <div><div className="tinylabel">Master data</div><h1 className="text-3xl font-black">Assignments</h1></div>
        <div className="flex items-center gap-3">
          <StatusFilter
            value={statusFilter}
            onChange={setStatusFilter}
            counts={statusCounts}
            options={[
              { value: "active", label: "Active" },
              { value: "completed", label: "Ended" },
              { value: "all", label: "All" },
            ]}
          />
          <ColumnChooser columns={ASN_COLUMNS} hiddenSet={hiddenSet} onToggle={toggleColumn} onReset={resetColumns} />
          {!readOnly && rows.some(needsNudge) && (
            <button data-testid="nudge-all-btn" onClick={nudgeAll} disabled={nudgingAll}
              title='Send an email + SMS reminder to every worker still showing "No timesheet" (workers marked "No work" are skipped).'
              className="h-11 px-4 border border-amber-300 text-amber-700 hover:bg-amber-50 font-semibold rounded-md flex items-center gap-2 disabled:opacity-60">
              <Bell size={16} weight="fill" />
              {nudgingAll ? "Nudging…" : `Nudge All (${rows.filter(needsNudge).length})`}
            </button>
          )}
          {!readOnly && <button data-testid="new-assignment-btn" onClick={() => { setEditingId(null); setForm(empty); setOtOn(false); setHolOn(false); setSendNotice(true); setWorkerEmail(""); setErr(""); setOpen(true); }}
            className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md flex items-center gap-2"><Plus size={18} /> New assignment</button>}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50">
            <tr>
              <SortHeader label="Worker"   testId="sort-worker"   active={sort.col === "worker"}   dir={sort.dir} onClick={() => toggleSort("worker")} />
              {vis("project") && <SortHeader label="Project"  testId="sort-project"  active={sort.col === "project"}  dir={sort.dir} onClick={() => toggleSort("project")} />}
              {vis("location") && <SortHeader label="Location" testId="sort-location" active={sort.col === "location"} dir={sort.dir} onClick={() => toggleSort("location")} />}
              {vis("po") && <SortHeader label="PO #"     testId="sort-po"       active={sort.col === "po"}       dir={sort.dir} onClick={() => toggleSort("po")} />}
              {vis("end") && <th></th>}
              {vis("pay") && <SortHeader label="Pay"      testId="sort-pay"      active={sort.col === "pay"}      dir={sort.dir} onClick={() => toggleSort("pay")} align="right" />}
              {vis("bill") && <SortHeader label="Bill"     testId="sort-bill"     active={sort.col === "bill"}     dir={sort.dir} onClick={() => toggleSort("bill")} align="right" />}
              {vis("per_diem") && <SortHeader label="Per Diem" testId="sort-per-diem" active={sort.col === "per_diem"} dir={sort.dir} onClick={() => toggleSort("per_diem")} align="right" />}
              {vis("ts") && <SortHeader label="Timesheet" testId="sort-ts"      active={sort.col === "ts"}       dir={sort.dir} onClick={() => toggleSort("ts")} />}
              {vis("status") && <SortHeader label="Status"   testId="sort-status"   active={sort.col === "status"}   dir={sort.dir} onClick={() => toggleSort("status")} />}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && <tr><td colSpan={asnVisCount} className="px-4 py-6 text-slate-400">No {statusFilter === "all" ? "" : statusFilter + " "}assignments{statusFilter !== "all" && rows.length > 0 ? " — switch the filter to see other statuses" : ""}.</td></tr>}
            {visibleRows.map((a) => (
              <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`assignment-row-${a.id}`}>
                <td className="px-4 py-3 font-medium">{lastFirst(wname(a.worker_id))}</td>
                {vis("project") && <td className="px-4 py-3">{pname(a.project_id)}</td>}
                {vis("location") && <td className="px-4 py-3 text-slate-600" data-testid={`assignment-location-${a.id}`}>{ploc(a.project_id) || "—"}</td>}
                {vis("po") && <td className="px-4 py-3 font-mono text-xs text-slate-600" data-testid={`assignment-po-${a.id}`}>{ponum(a.project_id) || "—"}</td>}
                {vis("end") && <td className="px-4 py-3 whitespace-nowrap">
                  {!readOnly && !isDone(a.status) ? (
                    <button data-testid={`end-assignment-${a.id}`} onClick={() => endAssignment(a)}
                      title="End this assignment — the worker will no longer see it when submitting timesheets."
                      className="h-8 px-3 border border-slate-300 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-100 hover:border-slate-400">
                      End assignment
                    </button>
                  ) : (
                    <span className="text-xs text-slate-300">—</span>
                  )}
                </td>}
                {vis("pay") && <td className="px-4 py-3 text-right font-mono">${a.pay_rate}</td>}
                {vis("bill") && <td className="px-4 py-3 text-right font-mono">${a.bill_rate}</td>}
                {vis("per_diem") && <td className="px-4 py-3 text-right font-mono">${a.per_diem_rate}</td>}
                {vis("ts") && <td className="px-4 py-3">{tsBadge(a)}</td>}
                {vis("status") && <td className="px-4 py-3"><StatusPill kind="assignment" status={isDone(a.status) ? "completed" : a.status} /></td>}
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {!readOnly && !isDone(a.status) && a.no_work_this_week && (
                    <button data-testid={`undo-no-work-${a.id}`} onClick={() => toggleNoWork(a, false)}
                      disabled={busyId === a.id}
                      title={`Marked "No work" for the week of ${weekRangeLabel(a._nudge_week)}. Click to undo — reminders resume.`}
                      className="text-blue-700 hover:text-blue-900 font-semibold mr-3 disabled:opacity-60 inline-flex items-center gap-1">
                      <CalendarX size={13} weight="bold" />
                      {busyId === a.id ? "Saving…" : "Undo no work"}
                    </button>
                  )}
                  {!readOnly && !isDone(a.status) && !a.no_work_this_week && (
                    <button data-testid={`nudge-ts-${a.id}`} onClick={() => nudgeTimesheet(a)}
                      disabled={nudgingId === a.id}
                      title={`Send an email + SMS to the worker asking them to submit their timesheet for last week (${weekRangeLabel(a._nudge_week)}).`}
                      className="text-amber-700 hover:text-amber-900 font-semibold mr-3 disabled:opacity-60 inline-flex items-center gap-1">
                      <Bell size={13} weight="fill" />
                      {nudgingId === a.id ? "Sending…" : "Nudge TS"}
                    </button>
                  )}
                  {!readOnly && !isDone(a.status) && !a.no_work_this_week && !a._nudge_week_has_ts && (
                    <button data-testid={`no-work-${a.id}`} onClick={() => toggleNoWork(a, true)}
                      disabled={busyId === a.id}
                      title={`Worker had no work the week of ${weekRangeLabel(a._nudge_week)} — pause reminders for that week.`}
                      className="text-blue-600 hover:text-blue-800 font-semibold mr-3 disabled:opacity-60 inline-flex items-center gap-1">
                      <CalendarX size={13} weight="bold" />
                      {busyId === a.id ? "Saving…" : "No work"}
                    </button>
                  )}
                  {!readOnly && !isDone(a.status) && (
                    <button data-testid={`resend-notice-${a.id}`} onClick={() => resendNotice(a)}
                      disabled={busyId === a.id}
                      title={a.notice_sent_at ? `Assignment notice last sent ${new Date(a.notice_sent_at).toLocaleString()} (${a.notice_count || 1}×). Click to resend.` : "Send the assignment notice (email + text with login & set-password links)."}
                      className="text-slate-500 hover:text-slate-800 font-semibold mr-3 disabled:opacity-60 inline-flex items-center gap-1">
                      <PaperPlaneTilt size={13} weight="bold" />
                      {a.notice_sent_at ? "Resend notice" : "Send notice"}
                    </button>
                  )}
                  {!readOnly && isDone(a.status) && (
                    <button data-testid={`reactivate-${a.id}`} onClick={() => reactivate(a)}
                      className="text-slate-600 hover:text-slate-900 font-semibold mr-3">Reactivate</button>
                  )}
                  {!readOnly && <button data-testid={`edit-assignment-${a.id}`} onClick={() => { setEditingId(a.id); setForm({ ...empty, ...a }); setOtOn(!!(a.ot_pay_rate || a.ot_bill_rate || a.ot_threshold_hours)); setHolOn(!!(a.holiday_pay_rate || a.holiday_bill_rate)); setOpen(true); }} className="text-green-600 hover:text-green-800 font-semibold">Edit</button>}
                  {!readOnly && (
                    <button data-testid={`delete-assignment-${a.id}`} onClick={() => del(a)}
                            title="Delete assignment"
                            className="ml-3 text-slate-400 hover:text-rose-600 align-middle">
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={save} className="bg-white rounded-md w-full max-w-lg p-6 border border-slate-200 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4"><h2 className="text-xl font-bold">{editingId ? "Edit assignment" : "New assignment"}</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={20} /></button></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Worker</label>
                <WorkerCombobox
                  value={form.worker_id}
                  onChange={(v) => setForm({ ...form, worker_id: v })}
                  workers={workers}
                  testId="assignment-input-worker"
                  required
                />
              </div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Project</label>
                <Combobox
                  value={form.project_id}
                  onChange={(v) => preset(v)}
                  options={sortClientsByName(projects).map((p) => ({
                    value: p.id, label: p.name, sub: p.po_number ? `PO ${p.po_number}` : "",
                    keywords: [p.po_number || ""],
                  }))}
                  testId="assignment-input-project"
                  placeholder="Select project…"
                  searchPlaceholder="Type project name or PO…"
                  required
                />
              </div>
              {["pay_rate", "bill_rate", "per_diem_rate"].map((k) => (
                <div key={k}><label className="text-xs font-bold uppercase tracking-widest text-slate-500">{k.replace(/_/g, " ")}</label>
                  <input type="number" step="0.01" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`assignment-input-${k}`} className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" /></div>
              ))}
              <div className="col-span-2 mt-1 pt-3 border-t border-slate-100">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={otOn} data-testid="assignment-ot-checkbox"
                    onChange={(e) => {
                      setOtOn(e.target.checked);
                      if (e.target.checked && !parseFloat(form.ot_threshold_hours)) setForm((f) => ({ ...f, ot_threshold_hours: 40 }));
                    }}
                    className="h-4 w-4 accent-green-600" />
                  <span className="tinylabel">Overtime applies</span>
                </label>
                {!otOn && <p className="text-xs text-slate-400 mt-1">Check to set OT pay/bill rates and a weekly threshold for this assignment.</p>}
                {otOn && <p className="text-xs text-slate-500 mt-1">Leave at 0 to inherit the project's OT config. Set values only if this worker's OT is different from the project defaults.</p>}
              </div>
              {otOn && (<>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">OT pay rate</label>
                <input type="number" step="0.01" value={form.ot_pay_rate || 0}
                  onChange={(e) => setForm({ ...form, ot_pay_rate: e.target.value })}
                  data-testid="assignment-input-ot_pay_rate"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">OT bill rate</label>
                <input type="number" step="0.01" value={form.ot_bill_rate || 0}
                  onChange={(e) => setForm({ ...form, ot_bill_rate: e.target.value })}
                  data-testid="assignment-input-ot_bill_rate"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">OT threshold (hrs)</label>
                <input type="number" step="0.5" value={form.ot_threshold_hours || 0}
                  onChange={(e) => setForm({ ...form, ot_threshold_hours: e.target.value })}
                  data-testid="assignment-input-ot_threshold_hours"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              </>)}
              <div className="col-span-2 mt-1 pt-3 border-t border-slate-100">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={holOn} data-testid="assignment-holiday-checkbox"
                    onChange={(e) => setHolOn(e.target.checked)}
                    className="h-4 w-4 accent-green-600" />
                  <span className="tinylabel">Holiday rates apply</span>
                </label>
                {!holOn && <p className="text-xs text-slate-400 mt-1">Check to set holiday pay/bill rates for this assignment.</p>}
                {holOn && <p className="text-xs text-slate-500 mt-1">Leave at 0 to inherit the project's holiday rates. Holiday <b>dates</b> are designated on the project itself.</p>}
              </div>
              {holOn && (<>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Holiday pay rate</label>
                <input type="number" step="0.01" value={form.holiday_pay_rate || 0}
                  onChange={(e) => setForm({ ...form, holiday_pay_rate: e.target.value })}
                  data-testid="assignment-input-holiday_pay_rate"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Holiday bill rate</label>
                <input type="number" step="0.01" value={form.holiday_bill_rate || 0}
                  onChange={(e) => setForm({ ...form, holiday_bill_rate: e.target.value })}
                  data-testid="assignment-input-holiday_bill_rate"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
              </div>
              </>)}
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Start date</label>
                <input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" /></div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Expected end <span className="normal-case tracking-normal font-normal text-slate-400">(optional)</span></label>
                <input type="date" value={form.expected_end_date || ""} onChange={(e) => setForm({ ...form, expected_end_date: e.target.value })}
                  data-testid="assignment-input-expected_end_date" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
                <div className="text-[11px] text-slate-400 mt-1">Shown to sales reps so they can judge when this worker frees up. Not enforced.</div></div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md"><option value="active">Active</option><option value="completed">Ended</option></select></div>
              {!editingId && (
                <div className="col-span-2 mt-1 pt-3 border-t border-slate-100">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" checked={sendNotice} data-testid="assignment-send-notice-checkbox"
                      onChange={(e) => { setSendNotice(e.target.checked); setErr(""); }}
                      className="h-4 w-4 mt-0.5 accent-green-600" />
                    <span>
                      <span className="tinylabel">Send assignment notice to worker</span>
                      <span className="block text-xs text-slate-500 mt-0.5">Email + text with the project, client, start date, a login link and a single-use “Set your password” link (7 days). A login is created automatically if they don't have one.</span>
                    </span>
                  </label>
                  {workerNeedsEmail && (
                    <div className="mt-3 bg-amber-50 border border-amber-300 rounded-md p-3" data-testid="assignment-no-email-panel">
                      <div className="text-sm font-semibold text-amber-900">{selectedWorker.name} has no email on file.</div>
                      <div className="text-xs text-amber-800 mt-0.5">The assignment will not be created until an email is added for the worker. Add it here to proceed (it's saved to their Worker record), or untick “Send assignment notice”.</div>
                      <input type="email" value={workerEmail} onChange={(e) => setWorkerEmail(e.target.value)}
                        placeholder="worker@example.com" data-testid="assignment-worker-email-input"
                        className="mt-2 w-full h-11 px-3 border border-amber-300 rounded-md bg-white" />
                    </div>
                  )}
                </div>
              )}
            </div>
            {err && <div className="text-red-600 text-sm mt-2">{err}</div>}
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" onClick={() => setOpen(false)} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
              <button data-testid="save-assignment-btn" className="h-11 px-4 bg-green-600 text-white font-semibold rounded-md">Save</button>
            </div>
          </form>
        </div>
      )}
      {endFor && (
        <EndAssignmentModal assignment={endFor} workerName={wname(endFor.worker_id)} projectName={pname(endFor.project_id)} others={othersOn(endFor)}
          onClose={() => setEndFor(null)}
          onDone={(data) => { setEndFor(null); load(); if (data.project_complete_requested) setEndResult(data); }} />
      )}
      {endResult && <ProjectNotCompletedDialog result={endResult} onClose={() => setEndResult(null)} />}
    </div>
  );
}
